<template>
  <SectionTemplate
    title="满足各行业多场景应用需求"
    class="page-main-scenarios"
    data-buried="Scenarios"
  >
    <div ref="tabsRef" class="scenarios-tabs">
      <div
        v-for="(tab, idx) in tabs"
        :key="tab.key"
        :class="['scenarios-tab', { active: idx === activeTab }]"
        @click="handleTabClick(idx)"
      >
        {{ tab.label }}
      </div>
    </div>
    <div ref="carouselRef" class="scenarios-carousel" @scroll="handleScroll">
      <div class="scenarios-cards">
        <div
          v-for="(card, idx) in allCards"
          :key="`${card.tabIndex}-${idx}`"
          :data-tab-index="card.tabIndex"
          :class="['scenarios-card', { 'is-active-tab': card.tabIndex === activeTab }]"
          @click="handleOpen(card, idx)"
        >
          <HoverVideo
            class="card-media"
            :src="card.video"
            :poster="card.img"
            :enable="true"
            :muted="true"
          />
          <div class="card-content">
            <div class="card-logo">
              <img :src="transformImage(card.logo)" alt="logo" />
            </div>
            <div class="card-title-row">
              <span class="card-title">{{ card.title }}</span>
            </div>
            <div class="card-desc">{{ card.desc }}</div>
            <button
              v-if="card.link"
              :title="card.title"
              class="card-btn"
              @click.stop="() => handleOpenLink(card.link)"
            >
              了解更多
              <ArrowRight class="card-btn-icon" />
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="scenarios-more-btn-row">
      <button class="btn-for-more" @click="handleStart">
        企业咨询
        <ArrowRight class="btn-for-more-arrow" />
      </button>
    </div>
  </SectionTemplate>
</template>

<script lang="ts" setup>
import { transformImage } from '@/utils/';
import { PositionCode, useContactUs } from '@rendora/common/features/contact';
import { useBreakpoint } from '@rendora/design/index';
import { computed, ref } from 'vue';
import ArrowRight from '~/assets/svg/icon-arrow-right.svg?component';
import SectionTemplate from '~/design/widgets/landing-section/cn-template.vue';
import HoverVideo from '../../components/hover-video.vue';

const headers = useRequestHeaders();
const { isMobile } = useBreakpoint(headers?.['user-agent']);

const handleStart = () => {
  useContactUs(
    PositionCode['有言官网-企业咨询'],
    '领取行业应用案例',
    '更多行业落地应用案例，扫码领取',
  );
};

const handleOpenLink = (link?: string) => {
  if (link) {
    window.open(link, '_blank');
  }
};

const tabs = [
  {
    key: 'training',
    label: '企业培训',
    cards: [
      {
        img: getFeUrl('/youyan-platform/main/scenarios/qypx-yl.png'),
        title: '伊利：业务内训师高效沉淀视频化知识库',
        desc: '有言通过AI数字人解决专家出镜难、效率低、更新慢等难题，助力伊利将PPT与文稿高效转化为专业培训视频，累计生成超2万分钟内容，实现核心知识的规模化沉淀与快速更新。',
        video: getFeUrl('/youyan-platform/main/scenarios/qypx-yl.webm'),
        logo: getFeUrl('/youyan-platform/main/logo2/yl.png'),
        link: 'https://youyan3d.com/customer-stories/How-AI-Digital-Humans-Boost-Corporate-Training',
      },
      {
        img: getFeUrl('/youyan-platform/main/scenarios/qypx-zx.png'),
        title: '中兴: 数千个B端客户培训视频批量生成，全面提效降本',
        desc: '有言助力中兴高效视频化2000门全球客户课程，覆盖多个语种与业务类型，解决传统录课成本高、风格不一、更新慢等问题，显著提升制作效率与交付一致性。',
        logo: getFeUrl('/youyan-platform/main/logo2/zx.png'),
        video: getFeUrl('/youyan-platform/main/scenarios-videos/zx.webm'),
        link: 'https://youyan3d.com/customer-stories/ZTE-Break-4B-Efficiency-Cycle-of-Training-Video-Production',
      },
      {
        img: getFeUrl('/youyan-platform/main/scenarios/qypx-skq.png'),
        title: '斯凯奇: AI视频打通门店培训最后一公里',
        desc: '借助AI数字人能力，斯凯奇将产品卖点、销售政策、销售技巧更清晰地展现给门店一线员工，提升部门的业务能力带来更多的销售转化。',
        logo: getFeUrl('/youyan-platform/main/logo2/skq.png'),
        video: getFeUrl('/youyan-platform/main/scenarios-videos/skq.webm'),
        link: 'https://youyan3d.com/customer-stories/Skechers-Ride-AIGC-Trend-to-Power-Sports-with-Smart-Brain',
      },
    ],
  },
  {
    key: 'marketing',
    label: '市场营销',
    cards: [
      {
        img: getFeUrl('/youyan-platform/main/scenarios/scyx-zrt.png'),
        title: '自然堂：突破明星SKU增长瓶颈，让更多产品被了解',
        desc: '通过魔珐有言生成产品讲解视频，用户可扫描产品外观盒的二维码观看视频，提升其对产品的理解和体验，增加购买率及复购率。',
        logo: getFeUrl('/youyan-platform/main/logo2/zrt.png'),
        video: getFeUrl('/youyan-platform/main/scenarios-videos/zrt.webm'),
        link: 'https://youyan3d.com/customer-stories/CHANDO-AIGC-Activates-Over-200-Increase-in-Repurchase',
      },
      {
        img: getFeUrl('/youyan-platform/main/scenarios/scyx-jj.png'),
        title: '劲酒：创新线上线下全平台运营模式',
        desc: '基于魔珐有言构建AI数字人“东方劲”“方晓静”，高效生成宣传片、产品介绍、节日运营等系列视频内容，覆盖线上社媒与线下大屏，实现品牌形象统一与用户高频触达。',
        logo: getFeUrl('/youyan-platform/main/logo2/jj.png'),
        video: getFeUrl('/youyan-platform/main/scenarios/jp.webm'),
        link: 'https://youyan3d.com/customer-stories/Jingpai-AI-Digital-Human-Interactive-Marketing-New-Way-for-Health-Wine-Enterprise',
      },
      {
        img: getFeUrl('/youyan-platform/main/scenarios/scyx-zl.png'),
        title: '中伦：AI驱动高效法律视频内容生产',
        desc: '打造3D数字人“爱伦ALLEN”，一键生成法律专业视频，统一传递品牌形象与复杂法理，开启律所内容表达与传播效率的双重跃升。',
        logo: getFeUrl('/youyan-platform/main/logo2/zl.png'),
        video: getFeUrl('/youyan-platform/main/scenarios-videos/zl.webm'),
        link: 'https://youyan3d.com/customer-stories/Zhonglun-Law-Firm-Rise-and-Efficient-Case-Handling',
      },
    ],
  },
  {
    key: 'education',
    label: '教育教学',
    cards: [
      {
        img: getFeUrl('/youyan-platform/main/scenarios/jyjx-sckfdx.png'),
        title: '四川开放大学：打造高质量的精品课程',
        desc: '教师用PPT即可生成课程视频，摆脱拍摄剪辑，显著提升课件制作效率与更新频率。多语种视频用于中俄交流年展示，课程在B站上线获高热度，实现课程生产提效与教育出海双突破。',
        logo: getFeUrl('/youyan-platform/main/logo2/sckfdx.png'),
        video: getFeUrl('/youyan-platform/main/scenarios/sckfdx.webm'),
        link: 'https://youyan3d.com/customer-stories/Sichuan-Open-University-How-College-Use-AIGC-for-Course',
      },
      {
        img: getFeUrl('/youyan-platform/main/scenarios/jyjx-zgrmjcdx.png'),
        title: '中国人民警察大学：打造“数字教官”教学新范式',
        desc: '借助数字人批量生成教学与宣教视频，解决警务课程协调难、制作慢、质量不统一等难题，实现课程体系化建设与传播力双提升，推动教学内容高效落地、对外宣传广泛出圈。',
        logo: getFeUrl('/youyan-platform/main/logo2/zgrmjc.png'),
        video: getFeUrl('/youyan-platform/main/scenarios/zgrmdx.webm'),
        link: 'https://youyan3d.com/customer-stories/Political-Legal-Education-How-AI-Digital-Human-Instructors-Achieve-Scale-and-External-Communication-of-Police-Academy-Courses',
      },
      {
        img: getFeUrl('/youyan-platform/main/scenarios/jyjx-fcwwlxx.png'),
        title: '富春湾未来小学：AI视频革新教学与传播模式',
        desc: '教学与宣传双重提效：老师可将PPT一键生成微课视频，提升线上导学效率；校务团队灵活制作高质量宣传片与通知视频，丰富校园表达，强化家校沟通。',
        logo: getFeUrl('/youyan-platform/main/logo2/fcwwlxx-1.png'),
        video: getFeUrl('/youyan-platform/main/scenarios-videos/fcwxx.webm'),
        link: 'https://youyan3d.com/customer-stories/Fuchunwan-Future-Primary-School-Embarks-on-Digital-Education-Journey',
      },
    ],
  },
  {
    key: 'gov',
    label: '政务服务',
    cards: [
      {
        img: getFeUrl('/youyan-platform/main/scenarios/zwfw-szgyyq.png'),
        title: '苏州工业园区：AI跑出政务服务“加速度',
        desc: '苏州工业园区携手有言打造AI数字人“小易”，集视频导办、政策咨询与交互指引于一体，实现全天候、可视化、高响应的智慧政务服务，显著提升群众办事效率与体验。',
        logo: getFeUrl('/youyan-platform/main/logo2/szgyyq.png'),
        video: getFeUrl('/youyan-platform/main/scenarios/szgyy.webm'),
        link: 'https://youyan3d.com/customer-stories/How-AI-Digital-Persona-Boosts-Government-Services-Xiaoyi-in-Suzhou-Industrial-Park',
      },
      {
        img: getFeUrl('/youyan-platform/main/scenarios/zwfw-hzsgaj.png'),
        title: '杭州市公安局：数智警察破题攻坚，AI赋能全方位立体化提效',
        desc: '打造“数智警察”，广泛应用于反诈宣传、警务培训、业务导办等场景，通过批量生成视频内容、升级服务流程，实现从“智宣”到“智治”的跃迁，推动警务工作全方位、立体化提效。',
        logo: getFeUrl('/youyan-platform/main/logo2/hzsgaj.png'),
        video: getFeUrl('/youyan-platform/main/scenarios-videos/hzsga.webm'),
        link: 'https://youyan3d.com/customer-stories/Hangzhou-Public-Security-Bureau-Creates-Anti-Fraud-Video-Series',
      },
      {
        img: getFeUrl('/youyan-platform/main/scenarios/zwfw-xhxcb.png'),
        title: '徐汇宣传部：数字宣传员“徐小宣”上岗',
        desc: '徐汇宣传部携手有言打造AI数字人“徐小宣”，作为“文明实践数字宣传员”，统一讲解政策、播报活动、导览展馆，实现政务传播与便民服务双升级，构建更亲民、更高效的智慧政务新模式。',
        logo: getFeUrl('/youyan-platform/main/logo2/xhxcb.png'),
        video: getFeUrl('/youyan-platform/main/scenarios/xhxcb.webm'),
        link: 'https://youyan3d.com/customer-stories/How-AI-Digital-Humans-Boost-Local-Government-PR-Xuhui-Digital-Anchors',
      },
    ],
  },
  {
    key: 'culture',
    label: '文旅宣传',
    cards: [
      {
        img: getFeUrl('/youyan-platform/main/scenarios/wlxc-qmwl.png'),
        title: '祁门文旅：数字人“群芳最”讲茶走红全网',
        desc: '围绕数字人“群芳最”，持续生成祁门红茶与本地文化视频，全域覆盖抖音、小红书等平台，实现从“讲好祁门茶”到“带火祁门游”的内容突围，激发文旅流量与茶产业消费双增长。',
        logo: getFeUrl('/youyan-platform/main/logo2/qmwl.png'),
        video: getFeUrl('/youyan-platform/main/scenarios-videos/qmwl.webm'),
        link: 'https://youyan3d.com/customer-stories/Qimen-Cultural-Tourism-AIGC-Decodes-Tea-Culture-Promotion',
      },
      {
        img: getFeUrl('/youyan-platform/main/scenarios/wlxc-cqhk.png'),
        title: '春秋航空：AI视频驱动内训外宣双引擎',
        desc: '春秋航空借助有言统一生产航线推荐、产品种草等营销视频，并系统化输出安全培训、业务流程等内部课程，实现高效内容供给，推动对内培训与对外传播效能双提升。',
        logo: getFeUrl('/youyan-platform/main/logo2/cqhk.png'),
        video: getFeUrl('/youyan-platform/main/scenarios-videos/cqhk.webm'),
        link: 'https://youyan3d.com/customer-stories/Spring-Airline-How-to-Do-Short-Video-Operation',
      },
      {
        img: getFeUrl('/youyan-platform/main/scenarios/wlxc-gchjdjt.png'),
        title: 'GCH酒店集团：数字人驱动智慧运营升级',
        desc: 'GCH携手有言推出数字人“Howard”，统一讲解培训内容、辅助门店决策、对外传播品牌形象，构建高效智能的集团运营体系。',
        logo: getFeUrl('/youyan-platform/main/logo2/gch.png'),
        video: getFeUrl('/youyan-platform/main/scenarios/dzhjd.webm'),
        link: 'https://youyan3d.com/customer-stories/How-to-Use-AI-Digital-Human-in-Hospitality-Industry-GCH-Introduces-Howard-for-Training-and-Customer-Service-Integration',
      },
    ],
  },
  {
    key: 'medical',
    label: '医疗医美',
    cards: [
      {
        img: getFeUrl('/youyan-platform/main/scenarios/ylym-aeyk.png'),
        title: '爱尔眼科：中央厨房式视频生产，带动公域获客',
        desc: '借助有言平台，爱尔眼科搭建“中央厨房式”视频生产中台，快速生成护眼科普、手术介绍等内容，覆盖600+门店与线上投放场景，帮助集团实现品牌统一、全域高效获客。',
        logo: getFeUrl('/youyan-platform/main/logo2/aeyk.png'),
        video: getFeUrl('/youyan-platform/main/scenarios-videos/aeyk.webm'),
        link: 'https://youyan3d.com/customer-stories/Aier-Eye-Hospital-Bulk-Reach-Precise-Customer-Locking-Cost-Drop-50',
      },
      {
        img: getFeUrl('/youyan-platform/main/scenarios/ylym-wzm.png'),
        title: '威兹曼：医美品牌数字人，突破品效瓶颈',
        desc: '打造品牌数字人Vivi，批量化生成短视频内容，通过视频号、自建商城等全域高频传播，实现品牌形象焕新与全域营销转化双增长。',
        logo: getFeUrl('/youyan-platform/main/logo2/wzm.png'),
        video: getFeUrl('/youyan-platform/main/scenarios-videos/wzm.webm'),
        link: 'https://youyan3d.com/customer-stories/Wiesmann-Secret-Weapon-to-Break-Brand-Influence-and-Marketing-Growth-Bottlenecks',
      },
      {
        img: getFeUrl('/youyan-platform/main/scenarios/ylym-swjk.png'),
        title: '汕尾疾控：破解基层宣教视频生产难题',
        desc: '将日常健康教育文章升级为AI数字人讲解视频，通过公众号和线下大厅循环播放，显著提升群众对防疫知识的关注度与理解力',
        logo: getFeUrl('/youyan-platform/main/logo2/swjk.png'),
        video: getFeUrl('/youyan-platform/main/scenarios/swjk.webm'),
        link: 'https://youyan3d.com/customer-stories/How-to-Make-AI-Digital-Person-Science-Videos-Youyan-Easily-Realizes-Batch-Health-Education-Videos',
      },
    ],
  },
  {
    key: 'finance',
    label: '金融理财',
    cards: [
      {
        img: getFeUrl('/youyan-platform/main/scenarios/jrlc-zjcf.png'),
        title: '中金财富：数字人“Jinn”年轻化投教内容表达',
        desc: '通过AI数字员工“Jinn”，中金财富在APP、视频号、小程序等渠道持续生成专业金融内容，覆盖资讯讲解、活动主持、投教科普等多个场景，实现品牌形象统一、多平台触达与陪伴式服务升级。',
        logo: getFeUrl('/youyan-platform/main/logo2/zjcf.png'),
        video: getFeUrl('/youyan-platform/main/scenarios-videos/zjcf.webm'),
        link: 'https://youyan3d.com/customer-stories/CICC-Wealth-Digital-Employee-Jinn-Reshapes-Financial-Services',
      },
      {
        img: getFeUrl('/youyan-platform/main/scenarios/jrlc-dwzq.png'),
        title: '东吴证券：AI快速生成每日财经资讯，观看效果超越真人录制',
        desc: '借助有言高频制作“东吴秀才说”等财经栏目视频，涵盖早报、中报、晚报。1分钟短视频平均停留时长达26秒，完播率达12.6%，实测效果优于真人录制，验证AI视频在专业表达中的认可度与传播力。',
        logo: getFeUrl('/youyan-platform/main/logo2/dwzq.png'),
        video: getFeUrl('/youyan-platform/main/scenarios/dw.webm'),
        link: 'https://youyan3d.com/customer-stories/Soochow-Securities-AIGC-Reshapes-Growth-Logic-with-Nearly-60-Growth',
      },
      {
        img: getFeUrl('/youyan-platform/main/scenarios/jrlc-szyh.png'),
        title: '苏州银行：AI数字人推动金融高质量发展',
        desc: '数字员工“苏盈盈”集品牌宣传、业务顾问、智能客服、文化宣导于一身，已广泛应用于苏州银行线上线下、内外部场景，先后主持吴中区战略签约仪式、参与苏州马拉松宣传，全面展现其金融科技创新实力。',
        logo: getFeUrl('/youyan-platform/main/logo2/szyh.png'),
        video: getFeUrl('/youyan-platform/main/scenarios-videos/szyh.webm'),
        link: 'https://youyan3d.com/customer-stories/Suzhou-Bank-Equips-AIGC-Booster-for-Tech-Branding',
      },
    ],
  },
];

interface Card {
  img: string;
  title: string;
  desc: string;
  logo: string;
  video: string;
  link?: string;
  tabIndex: number;
}

const activeTab = ref(0);
const carouselRef = ref<HTMLElement | null>(null);
const tabsRef = ref<HTMLElement | null>(null);
const isScrolling = ref(false);

// 合并所有 cards 为一个大数组，并标记所属的 tab 索引
const allCards = computed<Card[]>(() => {
  const cards: Card[] = [];
  tabs.forEach((tab, tabIndex) => {
    tab.cards.forEach(card => {
      cards.push({ ...card, tabIndex });
    });
  });
  return cards;
});

// 计算每个 tab 的起始卡片索引
const tabStartIndexes = computed(() => {
  const indexes: number[] = [];
  let currentIndex = 0;
  tabs.forEach(tab => {
    indexes.push(currentIndex);
    currentIndex += tab.cards.length;
  });
  return indexes;
});

// Tab 点击处理
const handleTabClick = (idx: number) => {
  activeTab.value = idx;
  // 移动端才滚动到对应卡片
  if (isMobile.value) {
    scrollToTab(idx);
  }
};

// 滚动到指定 tab 的第一个卡片
const scrollToTab = (tabIndex: number) => {
  if (!carouselRef.value) return;

  const startIndex = tabStartIndexes.value[tabIndex];
  const cards = carouselRef.value.querySelectorAll('.scenarios-card');
  const targetCard = cards[startIndex] as HTMLElement;

  if (targetCard) {
    isScrolling.value = true;
    const containerRect = carouselRef.value.getBoundingClientRect();
    const cardRect = targetCard.getBoundingClientRect();
    const scrollLeft = carouselRef.value.scrollLeft + cardRect.left - containerRect.left;

    carouselRef.value.scrollTo({
      left: scrollLeft,
      behavior: 'smooth',
    });

    // 滚动动画结束后重置标志
    setTimeout(() => {
      isScrolling.value = false;
    }, 500);
  }
};

// 处理滚动事件，更新当前激活的 tab（仅移动端）
const handleScroll = () => {
  if (!isMobile.value || isScrolling.value || !carouselRef.value) return;

  const container = carouselRef.value;
  const containerRect = container.getBoundingClientRect();
  const cards = container.querySelectorAll('.scenarios-card');

  // 找到当前视口中最左边可见的卡片
  let visibleTabIndex = 0;
  for (let i = 0; i < cards.length; i++) {
    const card = cards[i] as HTMLElement;
    const cardRect = card.getBoundingClientRect();
    const cardCenter = cardRect.left + cardRect.width / 2;

    // 如果卡片中心在容器左边界右侧，则认为这是当前可见的卡片
    if (cardCenter >= containerRect.left) {
      const tabIndex = parseInt(card.getAttribute('data-tab-index') || '0');
      visibleTabIndex = tabIndex;
      break;
    }
  }

  // 如果 tab 变化了，将对应的 tab 滚动到视口
  if (activeTab.value !== visibleTabIndex) {
    activeTab.value = visibleTabIndex;
    scrollTabIntoView(visibleTabIndex);
  }
};

// 将指定的 tab 滚动到视口中
const scrollTabIntoView = (tabIndex: number) => {
  if (!tabsRef.value) return;
  const tabElements = tabsRef.value.querySelectorAll('.scenarios-tab');
  const targetTab = tabElements[tabIndex] as HTMLElement;
  if (targetTab) {
    targetTab.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }
};

const open = ref(false);
const currentIndex = ref(0);

const handleOpen = (card: Card, idx: number) => {
  currentIndex.value = idx;
  open.value = true;
};
</script>
<style lang="scss" scoped src="./pc.scss" />
<style lang="scss" scoped src="./mobile.scss" />

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translate(-50%, calc(-50% + 50px));
  opacity: 0;
}
</style>
